import {
  ClipboardList,
  CreditCard,
  FileClock,
  LayoutDashboard,
  LogOut,
  MapPin,
  PackageOpen,
  Palette,
  ReceiptText,
  Settings,
  ShieldCheck,
  Sprout,
  Users,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from '../features/auth/AuthContext';
import { env } from '../lib/env';
import { initials } from '../lib/format';

/** The real storefront logo everywhere — no text stand-in, not even on loading screens. */
export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand${compact ? ' brand--compact' : ''}`}>
      <img
        alt="Tropikal Akdeniz"
        className="brand__logo"
        height={compact ? 34 : 46}
        src="https://tropikalakdeniz.com/cdn/shop/files/logo_yeni_boyut.png?height=71"
      />
    </span>
  );
}

export function PublicLayout() {
  return (
    <div className="public-shell">
      <a className="skip-link" href="#main-content">
        İçeriğe geç
      </a>
      <header className="public-header">
        <a aria-label="Tropikal Akdeniz mağazasına git" href={env.shopifyStorefrontUrl}>
          <Brand />
        </a>
        <a className="text-link" href={env.shopifyStorefrontUrl}>
          Mağazaya dön
        </a>
      </header>
      <main id="main-content">
        <Outlet />
      </main>
      <LegalFooter />
    </div>
  );
}

const adminNavigation = [
  { to: '/admin', end: true, label: 'Genel bakış', icon: LayoutDashboard },
  { to: '/admin/products', end: false, label: 'Ürünler ve fiyatlar', icon: PackageOpen },
  { to: '/admin/plans', end: false, label: 'Planlar', icon: Sprout },
  { to: '/admin/subscriptions', end: false, label: 'Abonelikler', icon: ClipboardList },
  { to: '/admin/payments', end: false, label: 'Ödeme sorunları', icon: CreditCard },
  { to: '/admin/order-sync', end: false, label: 'Sipariş aktarımı', icon: ReceiptText },
  { to: '/admin/customers', end: false, label: 'Müşteriler', icon: Users },
  { to: '/admin/branding', end: false, label: 'Görünüm', icon: Palette },
  { to: '/admin/audit', end: false, label: 'Denetim kaydı', icon: FileClock },
  { to: '/admin/settings', end: false, label: 'Ayarlar', icon: Settings },
] as const;

export function AdminLayout() {
  const { user, signOut } = useAuth();

  return (
    <div className="admin-shell">
      <a className="skip-link" href="#admin-content">
        İçeriğe geç
      </a>
      <aside className="admin-sidebar">
        <NavLink aria-label="Yönetim ana sayfası" className="admin-sidebar__brand" to="/admin">
          <Brand compact />
          <span className="admin-pill">
            <ShieldCheck aria-hidden="true" size={14} /> Yönetim
          </span>
        </NavLink>
        <nav aria-label="Yönetim menüsü" className="admin-nav">
          {adminNavigation.map(({ to, end, label, icon: Icon }) => (
            <NavLink end={end} key={to} to={to}>
              <Icon aria-hidden="true" size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar__account">
          <span aria-hidden="true" className="avatar avatar--dark">
            {initials(user?.displayName ?? 'Yönetici')}
          </span>
          <div>
            <strong>{user?.displayName}</strong>
            <small>{user?.email}</small>
          </div>
          <button aria-label="Oturumu kapat" className="icon-button" onClick={() => void signOut()}>
            <LogOut aria-hidden="true" size={18} />
          </button>
        </div>
      </aside>
      <main className="admin-main" id="admin-content">
        <div className="admin-mobile-header">
          <Brand compact />
          <span className="admin-pill">Yönetim</span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

function LegalFooter() {
  return (
    <footer className="legal-footer">
      <span>© {new Date().getFullYear()} Tropikal Akdeniz</span>
      <nav aria-label="Yasal bağlantılar">
        <NavLink to="/legal/subscription-terms">Abonelik koşulları</NavLink>
        <NavLink to="/legal/privacy">Gizlilik</NavLink>
        <NavLink to="/legal/distance-sales">Mesafeli satış</NavLink>
      </nav>
      <span className="legal-footer__secure">
        <ShieldCheck aria-hidden="true" size={16} /> Güvenli portal
      </span>
    </footer>
  );
}

export function AddressIcon() {
  return <MapPin aria-hidden="true" size={19} />;
}
